/** @file Generated form for one command: fields from its input schema; Apply validates, then submits. */
import { useMemo, useState } from 'react';
import { makeError } from '@pose2d/format/pose2dError';
import type { CommandCall, Doc, Pose2DError } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { errorText } from '../shell/i18n/errorText';
import { Button } from '../ui-kit/Button';
import { buildInput } from './buildInput';
import { commandSchema } from './commandSchema';
import { fieldAtPath } from './fieldAtPath';
import { FieldInput } from './FieldInput';
import { FormStatus } from './FormStatus';
import { initialValues } from './initialValues';
import type { SubmitResult } from './submitCalls';
import { validateInput } from './validateInput';
import './forms.css';

/** Props of CommandForm; pass stable `prefill`/`hidden` objects (memoized) so edits survive re-renders. */
export interface CommandFormProps {
  /** A registered command, e.g. `slot.setBlend`. */
  command: string;
  /** The document reference selects list names from. */
  doc: Doc;
  /** Start values in the command's input shape (reference ids allowed); unknown keys are ignored. */
  prefill?: Readonly<Record<string, unknown>>;
  /** Top-level keys not shown, still sent from the prefill (e.g. `target`); `id` is never shown. */
  hidden?: readonly string[];
  /** Sends the call; resolves to the refusal (shown in place) or success. */
  onSubmit: (call: CommandCall) => Promise<SubmitResult>;
  t: Translate;
}

const NO_PREFILL = {};
const NO_HIDDEN: readonly string[] = [];

/** Description, fields, Apply (enabled after an edit, or at once when no field is shown) and the outcome. */
export function CommandForm({ command, doc, prefill = NO_PREFILL, hidden = NO_HIDDEN, onSubmit, t }: CommandFormProps) {
  const schema = commandSchema(command);
  const shown = useMemo(() => schema.fields.filter((f) => f.key !== 'id' && !hidden.includes(f.key)), [schema, hidden]);
  const base = useMemo(() => initialValues(schema.fields, prefill, doc), [schema, prefill, doc]);
  const [edits, setEdits] = useState<ReadonlyMap<string, unknown>>(new Map());
  const [status, setStatus] = useState<SubmitResult | null>(null);
  const values = useMemo(() => new Map([...base, ...edits]), [base, edits]);
  const apply = async (): Promise<void> => {
    const built = buildInput(schema.fields, values, new Set(edits.keys()));
    const bad = built.badJson[0];
    const invalid: Pose2DError | null = bad === undefined ? validateInput(command, built.input) : makeError('VALIDATION', undefined, { path: bad, hint: t('forms.invalidJson') });
    const result = invalid === null ? await onSubmit({ name: command, input: built.input }) : { error: invalid, warnings: [] };
    setStatus(result);
    if (result.error === null) setEdits(new Map());
  };
  const at = fieldAtPath(shown, status?.error?.path);
  const error = at === null || !status?.error ? null : { at, text: status.error.hint ?? errorText(t, { pose2d: status.error }) };
  return (
    <form className="p2-form" aria-label={command} onSubmit={(e) => { e.preventDefault(); void apply(); }}>
      <p className="p2-form-description">{schema.description}</p>
      {shown.map((field) => <FieldInput key={field.key} field={field} values={values} doc={doc} t={t} error={error} onEdit={(path, v) => { setEdits(new Map(edits).set(path, v)); }} />)}
      <Button label={t('forms.apply')} primary disabled={shown.length > 0 && edits.size === 0} onClick={() => { void apply(); }} />
      <FormStatus status={status} t={t} showError={error === null} />
    </form>
  );
}
